import { useEffect, useRef, useState } from "react";
import { Users, Briefcase, ClipboardCheck, TrendingUp } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { upsertUserAttributes } from "@/lib/userAttributes";
import { trackEvent } from "@/lib/analytics";

// One-question-at-a-time profile completion inside Stack results. Members skip
// the separate profile page, but answer when the question sits next to the
// Stack they came for.

const SKIP_KEY = "ha_stack_profile_q_skipped";
const HOUSEHOLD = ["Just me", "2", "3", "4", "5+"];
const EMPLOYER_CATEGORY = [
  { value: "federal", label: "Federal government" },
  { value: "state", label: "State government" },
  { value: "county", label: "County / city" },
  { value: "education", label: "Education" },
  { value: "healthcare", label: "Healthcare" },
  { value: "nonprofit", label: "Nonprofit" },
  { value: "private", label: "Private company" },
  { value: "self_employed", label: "Self-employed" },
  { value: "other", label: "Other" },
];

type QKey = "preapproval" | "household" | "employer";

const PREAPPROVAL = ["Already pre-approved", "In progress", "Not started"];

function isLowCredit(range?: string | null): boolean {
  if (!range) return false;
  const r = range.toLowerCase().replace(/_/g, "-");
  if (/below[- ]?(580|620)|under[- ]?(580|620)/.test(r)) return true;
  const m = r.match(/(\d{3})\s*[-–]\s*(\d{3})/);
  return m ? parseInt(m[2], 10) < 620 : false;
}

interface Props {
  className?: string;
  onSaved?: (attrs: Record<string, string>) => void;
}

export default function StackProfileQuestion({ className = "", onSaved }: Props) {
  const { user } = useAuth();
  const [queue, setQueue] = useState<QKey[]>([]);
  const [employerName, setEmployerName] = useState("");
  const [saving, setSaving] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  const [lowCredit, setLowCredit] = useState(false);
  const shown = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user) { setQueue([]); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("user_attributes")
        .select("household_size_bucket, employer_category, employer_name, pre_approval_status, credit_score_range")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      let skipped: string[] = [];
      try { skipped = JSON.parse(localStorage.getItem(SKIP_KEY) || "[]"); } catch { /* ignore */ }
      const a: any = data ?? {};
      const q: QKey[] = [];
      setLowCredit(isLowCredit(a.credit_score_range));
      if (!a.pre_approval_status && !skipped.includes("preapproval")) q.push("preapproval");
      if (!a.household_size_bucket && !skipped.includes("household")) q.push("household");
      if (!a.employer_category && !a.employer_name && !skipped.includes("employer")) q.push("employer");
      setQueue(q);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const current = queue[0];
  useEffect(() => {
    if (current && !shown.current.has(current)) {
      shown.current.add(current);
      trackEvent("profile_question_shown", { question: current, placement: "stack_results" });
    }
  }, [current]);

  const creditFirst = lowCredit ? (
    <Card className={`p-4 border-primary/30 bg-primary/5 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="size-9 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          <TrendingUp className="size-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground">Your best first step: build your credit</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            Most of these programs need a credit score of 620–640. Free HUD-approved housing counseling in your Stack can help you get there,
            often in 60–90 days. Keep cards under 30% of their limit and avoid opening new accounts.
          </p>
          <Link to="/credit-readiness" className="mt-2 inline-block text-sm font-medium text-primary underline"
            onClick={() => trackEvent("credit_first_click", { placement: "stack_results" })}>
            See my credit plan
          </Link>
        </div>
      </div>
    </Card>
  ) : null;

  if (!user || !current) {
    return (
      <>
        {creditFirst}
        {doneCount > 0 ? (
          <p className={`text-sm text-muted-foreground ${className}`}>Thanks — your Stack now uses your answers.</p>
        ) : null}
      </>
    );
  }

  const save = async (attrs: Record<string, string>) => {
    setSaving(true);
    try {
      await upsertUserAttributes(attrs);
      trackEvent("profile_question_answered", { question: current, placement: "stack_results" });
      onSaved?.(attrs);
      setDoneCount((n) => n + 1);
      setQueue((q) => q.slice(1));
    } finally {
      setSaving(false);
    }
  };

  const skip = () => {
    try {
      const s: string[] = JSON.parse(localStorage.getItem(SKIP_KEY) || "[]");
      localStorage.setItem(SKIP_KEY, JSON.stringify([...new Set([...s, current])]));
    } catch { /* ignore */ }
    trackEvent("profile_question_skipped", { question: current, placement: "stack_results" });
    setQueue((q) => q.slice(1));
  };

  const isHousehold = current === "household";
  const isPre = current === "preapproval";
  const Icon = isPre ? ClipboardCheck : isHousehold ? Users : Briefcase;

  return (
    <>
    {creditFirst}
    <Card className={`p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="size-9 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="size-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground">
            {isPre ? "Where are you with mortgage pre-approval?" : isHousehold ? "How many people live in your home?" : "Who do you work for?"}
          </p>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isPre
              ? "This tells us whether you're ready for a lender or agent, or whether a free counseling step comes first."
              : isHousehold
              ? "Many grants set income limits by household size. This sharpens what you may qualify for."
              : "Some employers offer homebuying benefits. This checks the employer layer of your Stack."}
          </p>

          {isPre ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {PREAPPROVAL.map((v) => (
                <Button key={v} size="sm" variant="outline" disabled={saving}
                  onClick={() => save({ pre_approval_status: v })}>{v}</Button>
              ))}
            </div>
          ) : isHousehold ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {HOUSEHOLD.map((h) => (
                <Button key={h} size="sm" variant="outline" disabled={saving}
                  onClick={() => save({ household_size_bucket: h })}>{h}</Button>
              ))}
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              <div className="flex flex-wrap gap-2">
                {EMPLOYER_CATEGORY.map((c) => (
                  <Button key={c.value} size="sm" variant="outline" disabled={saving}
                    onClick={() => save({ employer_category: c.value, ...(employerName.trim() ? { employer_name: employerName.trim() } : {}) })}>
                    {c.label}
                  </Button>
                ))}
              </div>
              <div className="space-y-1">
                <Label htmlFor="stack-employer-name" className="text-xs text-muted-foreground">Employer name (helps us check specific benefits)</Label>
                <Input id="stack-employer-name" maxLength={120} placeholder="e.g. NYC Department of Education"
                  value={employerName} onChange={(e) => setEmployerName(e.target.value)} />
              </div>
            </div>
          )}

          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span>{queue.length === 1 ? "Last question" : `${queue.length} quick questions left`}</span>
            <button type="button" className="underline hover:text-foreground" onClick={skip}>Not now</button>
          </div>
        </div>
      </div>
    </Card>
    </>
  );
}
